import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Plus,
  Save,
  Trash2,
  Users,
  Search,
  Eye,
  Code2,
  BookOpen,
} from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { fetchCoursesFromDb } from '../../services/coursesService';
import {
  COURSE_EMAIL_DELIVERY_MODES,
  COURSE_EMAIL_DELIVERY_MODE_VALUES,
  courseEmailContentService,
  deliveryModeLabel,
  type CourseEmailContentAssignment,
  type CourseEmailContentBlock,
  type CourseEmailDeliveryMode,
} from '../../services/courseEmailContentService';
import { FloatingWorkspacePanel } from '../ui/FloatingWorkspacePanel';

interface ManageCourseEmailContentModalProps {
  onClose: () => void;
}

const WORDING_PLACEHOLDER_HINTS = [
  '{{CourseName}}',
  '{{CourseDate}}',
  '{{StartTime}}',
  '{{Location}}',
  '{{ParticipantList}}',
  '{{ParticipantCount}}',
  '{{CompanyName}}',
];

function getWordingDefaultBounds() {
  if (typeof window === 'undefined') {
    return { x: 520, y: 28, width: 880, height: 820 };
  }
  const vw = window.innerWidth;
  return {
    x: Math.max(420, Math.round(vw * 0.42)),
    y: 28,
    width: Math.min(920, Math.round(vw * 0.52)),
    height: Math.min(820, Math.round(window.innerHeight * 0.88)),
  };
}

function getAssignDefaultBounds() {
  if (typeof window === 'undefined') {
    return { x: 200, y: 80, width: 720, height: 640 };
  }
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(900, Math.round(vw * 0.55));
  const height = Math.min(680, Math.round(vh * 0.78));
  return {
    x: Math.round((vw - width) / 2),
    y: Math.round((vh - height) / 2),
    width,
    height,
  };
}

function uniqueCourseNames(assignments: CourseEmailContentAssignment[]): string[] {
  return [...new Set(assignments.map((a) => a.courseName))].sort((a, b) => a.localeCompare(b));
}

function modesForAssignments(assignments: CourseEmailContentAssignment[]): CourseEmailDeliveryMode[] {
  const present = new Set(assignments.map((a) => a.deliveryMode));
  if (present.size === 0) return [...COURSE_EMAIL_DELIVERY_MODE_VALUES];
  return COURSE_EMAIL_DELIVERY_MODE_VALUES.filter((m) => present.has(m));
}

function groupedAssignmentChips(assignments: CourseEmailContentAssignment[]): Array<{
  courseName: string;
  modesLabel: string;
}> {
  const byCourse = new Map<string, CourseEmailDeliveryMode[]>();
  for (const row of assignments) {
    const list = byCourse.get(row.courseName) || [];
    if (!list.includes(row.deliveryMode)) list.push(row.deliveryMode);
    byCourse.set(row.courseName, list);
  }
  return [...byCourse.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([courseName, modes]) => {
      const allModes = modes.length === COURSE_EMAIL_DELIVERY_MODE_VALUES.length;
      return {
        courseName,
        modesLabel: allModes
          ? 'all modes'
          : modes.map((m) => deliveryModeLabel(m)).join(', '),
      };
    });
}

interface AssignCoursesPanelProps {
  blockId: string;
  blockName: string;
  assigned: CourseEmailContentAssignment[];
  allCourses: string[];
  onClose: () => void;
  onSaved: (assignments: CourseEmailContentAssignment[]) => void;
}

function AssignCoursesPanel({
  blockId,
  blockName,
  assigned,
  allCourses,
  onClose,
  onSaved,
}: AssignCoursesPanelProps) {
  const { showSuccess, showError } = useToast();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(() => new Set(uniqueCourseNames(assigned)));
  const [selectedModes, setSelectedModes] = useState<Set<CourseEmailDeliveryMode>>(
    () => new Set(modesForAssignments(assigned)),
  );
  const [saving, setSaving] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allCourses;
    return allCourses.filter((c) => c.toLowerCase().includes(q));
  }, [allCourses, search]);

  const toggle = (name: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const toggleMode = (mode: CourseEmailDeliveryMode) => {
    setSelectedModes((prev) => {
      const next = new Set(prev);
      if (next.has(mode)) next.delete(mode);
      else next.add(mode);
      return next;
    });
  };

  const handleSave = async () => {
    if (selected.size > 0 && selectedModes.size === 0) {
      showError('Select at least one delivery mode.');
      return;
    }
    setSaving(true);
    try {
      const saved = await courseEmailContentService.setBlockCourses(
        blockId,
        [...selected],
        [...selectedModes],
      );
      showSuccess('Course assignments saved.');
      onSaved(saved);
      onClose();
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Could not save assignments');
    } finally {
      setSaving(false);
    }
  };

  return (
    <FloatingWorkspacePanel
      panelId="course-email-assign"
      title="Assign courses"
      subtitle={<>Block: {blockName}</>}
      defaultBounds={getAssignDefaultBounds()}
      minSize={{ width: 480, height: 360 }}
      backdrop="none"
      baseZIndex={10002}
      onClose={onClose}
      footer={
        <div className="flex items-center justify-between px-5 py-3">
          <span className="text-sm text-slate-600">
            {selected.size} course(s) · {selectedModes.size} delivery mode(s)
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => void handleSave()}
              className="inline-flex items-center rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              <Save className="mr-2 h-4 w-4" />
              {saving ? 'Saving…' : 'Save assignments'}
            </button>
          </div>
        </div>
      }
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="shrink-0 border-b border-slate-100 px-5 py-3">
          <p className="mb-2 text-xs font-semibold text-slate-600">Applies to delivery modes</p>
          <div className="mb-3 flex flex-wrap gap-2">
            {COURSE_EMAIL_DELIVERY_MODES.map((mode) => (
              <label
                key={mode.value}
                className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 text-sm ${
                  selectedModes.has(mode.value)
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                    : 'border-slate-200 bg-white text-slate-700'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selectedModes.has(mode.value)}
                  onChange={() => toggleMode(mode.value)}
                  className="h-4 w-4 rounded border-slate-300 text-emerald-600"
                />
                {mode.label}
              </label>
            ))}
          </div>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search courses…"
              className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm"
            />
          </div>
          <p className="mt-2 text-xs text-slate-500">
            A course can belong to one block per delivery mode. Saving here replaces this block's
            assignments with the selected courses for the selected modes, and moves those pairs off any
            other block.
          </p>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {filtered.length === 0 ? (
            <p className="text-sm text-slate-500">No courses match your search.</p>
          ) : (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {filtered.map((course) => (
                <label
                  key={course}
                  className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(course)}
                    onChange={() => toggle(course)}
                    className="h-4 w-4 rounded border-slate-300 text-emerald-600"
                  />
                  <span className="text-sm text-slate-800">{course}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    </FloatingWorkspacePanel>
  );
}

export function ManageCourseEmailContentModal({ onClose }: ManageCourseEmailContentModalProps) {
  const { showSuccess, showError } = useToast();
  const [blocks, setBlocks] = useState<CourseEmailContentBlock[]>([]);
  const [allCourses, setAllCourses] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState('');
  const [htmlDraft, setHtmlDraft] = useState('');
  const [assigned, setAssigned] = useState<CourseEmailContentAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);

  const selectedBlock = blocks.find((b) => b.id === selectedId) || null;
  const assignmentChips = useMemo(() => groupedAssignmentChips(assigned), [assigned]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [blockRows, courseRows] = await Promise.all([
        courseEmailContentService.listBlocks(),
        fetchCoursesFromDb(),
      ]);
      setBlocks(blockRows);
      setAllCourses(
        courseRows
          .map((r) => String(r.name || '').trim())
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b)),
      );
      setSelectedId((prev) => {
        if (prev && blockRows.some((b) => b.id === prev)) return prev;
        return blockRows[0]?.id || null;
      });
    } catch (e) {
      console.error(e);
      showError('Could not load course email wording.');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    void loadData();
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setNameDraft('');
      setHtmlDraft('');
      setAssigned([]);
      return;
    }
    const block = blocks.find((b) => b.id === selectedId);
    if (!block) return;
    setNameDraft(block.name);
    setHtmlDraft(block.htmlContent);
    void courseEmailContentService.getAssignmentsForBlock(selectedId).then(setAssigned);
  }, [selectedId, blocks]);

  const handleSelectBlock = (id: string) => {
    setSelectedId(id);
    setShowPreview(false);
  };

  const handleAddBlock = async () => {
    try {
      const created = await courseEmailContentService.upsertBlock({
        name: 'New wording block',
        htmlContent: '',
      });
      setBlocks((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      setSelectedId(created.id);
      showSuccess('Block created.');
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Could not create block');
    }
  };

  const handleSaveBlock = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const updated = await courseEmailContentService.upsertBlock({
        id: selectedId,
        name: nameDraft,
        htmlContent: htmlDraft,
      });
      setBlocks((prev) =>
        prev
          .map((b) => (b.id === updated.id ? { ...updated, courseCount: b.courseCount } : b))
          .sort((a, b) => a.name.localeCompare(b.name)),
      );
      showSuccess('Block saved.');
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Could not save block');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBlock = async () => {
    if (!selectedId || !selectedBlock) return;
    if (
      !window.confirm(
        `Delete "${selectedBlock.name}"? ${selectedBlock.courseCount} course(s) will be unassigned.`,
      )
    ) {
      return;
    }
    try {
      await courseEmailContentService.deleteBlock(selectedId);
      const remaining = blocks.filter((b) => b.id !== selectedId);
      setBlocks(remaining);
      setSelectedId(remaining[0]?.id || null);
      showSuccess('Block deleted.');
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Could not delete block');
    }
  };

  const insertPlaceholder = (token: string) => {
    setHtmlDraft((prev) => (prev ? `${prev}${prev.endsWith(' ') ? '' : ' '}${token}` : token));
  };

  return (
    <>
      <FloatingWorkspacePanel
        panelId="course-email-wording"
        title="Course email wording"
        subtitle={
          <>
            Shared HTML blocks — use{' '}
            <code className="rounded bg-slate-100 px-1 text-[11px]">{'{{CourseEmailContent}}'}</code> in templates.
            Assign the same course to different blocks per delivery mode.
          </>
        }
        icon={
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600">
            <BookOpen className="h-4 w-4 text-white" />
          </div>
        }
        defaultBounds={getWordingDefaultBounds()}
        minSize={{ width: 560, height: 420 }}
        backdrop="none"
        baseZIndex={10001}
        onClose={onClose}
      >
        {loading ? (
          <div className="flex flex-1 items-center justify-center text-slate-500">Loading…</div>
        ) : (
          <div className="flex min-h-0 flex-1">
            <aside className="flex w-[280px] shrink-0 flex-col border-r border-slate-200 bg-slate-50/80">
              <div className="border-b border-slate-200 p-3">
                <button
                  type="button"
                  onClick={() => void handleAddBlock()}
                  className="inline-flex w-full items-center justify-center rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add block
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-2">
                {blocks.length === 0 ? (
                  <p className="px-2 py-4 text-xs text-slate-500">No blocks yet. Add one to get started.</p>
                ) : (
                  blocks.map((block) => (
                    <button
                      key={block.id}
                      type="button"
                      onClick={() => handleSelectBlock(block.id)}
                      className={`mb-1 w-full rounded-lg px-3 py-2.5 text-left transition-colors ${
                        selectedId === block.id
                          ? 'bg-emerald-600 text-white shadow-sm'
                          : 'bg-white text-slate-800 hover:bg-slate-100'
                      }`}
                    >
                      <div className="truncate text-sm font-medium">{block.name}</div>
                      <div
                        className={`text-xs ${selectedId === block.id ? 'text-emerald-100' : 'text-slate-500'}`}
                      >
                        {block.courseCount} course{block.courseCount === 1 ? '' : 's'}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </aside>

            <main className="flex min-w-0 flex-1 flex-col">
              {!selectedBlock ? (
                <div className="flex flex-1 items-center justify-center text-slate-500">
                  Select or create a wording block
                </div>
              ) : (
                <>
                  <div className="shrink-0 border-b border-slate-200 px-5 py-3">
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="min-w-[200px] flex-1">
                        <label className="mb-1 block text-xs font-semibold text-slate-600">Block name</label>
                        <input
                          type="text"
                          value={nameDraft}
                          onChange={(e) => setNameDraft(e.target.value)}
                          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                          placeholder="e.g. Standard practical wording"
                        />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => setShowAssignModal(true)}
                          className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                        >
                          <Users className="mr-2 h-4 w-4" />
                          Assign courses
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowPreview((v) => !v)}
                          className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                        >
                          {showPreview ? (
                            <>
                              <Code2 className="mr-2 h-4 w-4" />
                              Hide preview
                            </>
                          ) : (
                            <>
                              <Eye className="mr-2 h-4 w-4" />
                              Preview
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => void handleSaveBlock()}
                          className="inline-flex items-center rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
                        >
                          <Save className="mr-2 h-4 w-4" />
                          {saving ? 'Saving…' : 'Save block'}
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleDeleteBlock()}
                          className="inline-flex items-center rounded-lg border border-red-200 px-3 py-2 text-sm text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </button>
                      </div>
                    </div>

                    {assignmentChips.length > 0 && (
                      <div className="mt-3">
                        <p className="mb-1 text-xs font-semibold text-slate-600">Assigned courses</p>
                        <div className="flex max-h-20 flex-wrap gap-1 overflow-y-auto">
                          {assignmentChips.map((chip) => (
                            <span
                              key={chip.courseName}
                              className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-800 ring-1 ring-emerald-100"
                            >
                              {chip.courseName} · {chip.modesLabel}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className={`grid min-h-0 flex-1 ${showPreview ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    <div className="flex min-h-0 flex-col border-r border-slate-100 p-4">
                      <label className="mb-1 text-xs font-semibold text-slate-600">HTML content</label>
                      <p className="mb-2 text-[11px] text-slate-500">
                        Placeholders in this block are filled when the email is sent. Click to insert:
                      </p>
                      <div className="mb-2 flex flex-wrap gap-1">
                        {WORDING_PLACEHOLDER_HINTS.map((token) => (
                          <button
                            key={token}
                            type="button"
                            onClick={() => insertPlaceholder(token)}
                            className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px] text-slate-700 hover:bg-slate-100"
                          >
                            {token}
                          </button>
                        ))}
                      </div>
                      <textarea
                        value={htmlDraft}
                        onChange={(e) => setHtmlDraft(e.target.value)}
                        spellCheck={false}
                        className="min-h-[200px] flex-1 resize-none rounded-lg border border-slate-300 bg-white p-3 font-mono text-sm leading-relaxed"
                        placeholder="<p>Course-specific requirements, payment deadlines, what to bring…</p>"
                      />
                    </div>
                    {showPreview && (
                      <div className="flex min-h-0 flex-col p-4">
                        <label className="mb-2 text-xs font-semibold text-slate-600">Preview</label>
                        <div className="min-h-[200px] flex-1 overflow-y-auto rounded-lg border border-slate-200 bg-white p-4 text-sm prose prose-sm max-w-none">
                          {htmlDraft.trim() ? (
                            <div dangerouslySetInnerHTML={{ __html: htmlDraft }} />
                          ) : (
                            <p className="text-slate-400">Nothing to preview yet.</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </main>
          </div>
        )}
      </FloatingWorkspacePanel>

      {showAssignModal && selectedId && selectedBlock && (
        <AssignCoursesPanel
          blockId={selectedId}
          blockName={nameDraft || selectedBlock.name}
          assigned={assigned}
          allCourses={allCourses}
          onClose={() => setShowAssignModal(false)}
          onSaved={(next) => {
            setAssigned(next);
            void courseEmailContentService.listBlocks().then(setBlocks);
          }}
        />
      )}
    </>
  );
}
